'use client';

import { StatusBadge } from '@/components/common';
import { ItemTrackingStatus } from '@/domain/roadmap/types';
import { getStatusColor } from '@/features/roadmap/components/RoadMapEditDialog/PlanningTab/utils';
import { Divider } from '@andritzot/metris-web-ui/data-display/divider';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { FormControl } from '@andritzot/metris-web-ui/inputs/form-control';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { InputLabel } from '@andritzot/metris-web-ui/inputs/input-label';
import { Select } from '@andritzot/metris-web-ui/inputs/select';
import ContentSidebar from '@andritzot/metris-web-ui/layout/content-sidebar';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { MenuItem } from '@andritzot/metris-web-ui/navigation/menu/menu-item';
import CloseIcon from '@mui/icons-material/Close';
import { Fragment, useState } from 'react';

const ITEM_TRACKING_STATUS_VALUES: ReadonlySet<string> = new Set<string>(Object.values(ItemTrackingStatus));

function isItemTrackingStatus(value: unknown): value is ItemTrackingStatus {
  return typeof value === 'string' && ITEM_TRACKING_STATUS_VALUES.has(value);
}

function getStatusEntries(summary: Partial<Record<ItemTrackingStatus, number>>): [ItemTrackingStatus, number][] {
  return Object.entries(summary).filter((entry): entry is [ItemTrackingStatus, number] =>
    isItemTrackingStatus(entry[0]),
  );
}

const STATUS_OPTIONS: ItemTrackingStatus[] = [
  ItemTrackingStatus.NotStarted,
  ItemTrackingStatus.InProgress,
  ItemTrackingStatus.Completed,
  ItemTrackingStatus.Delayed,
  ItemTrackingStatus.NotApplicable,
];

interface QaSummary {
  approved: number;
  pending: number;
}

interface PlanningViewSideBarProps {
  selectedCount: number;
  statusSummary: Partial<Record<ItemTrackingStatus, number>>;
  qaSummary: QaSummary;
  onApply: (
    perGroupMap: Partial<Record<ItemTrackingStatus, ItemTrackingStatus>>,
    qaPerGroupMap: Partial<Record<string, boolean>>,
  ) => void;
  onClose: () => void;
}

export function PlanningViewSideBar({
  selectedCount,
  statusSummary,
  qaSummary,
  onApply,
  onClose,
}: Readonly<PlanningViewSideBarProps>) {
  const [perGroupTarget, setPerGroupTarget] = useState<Partial<Record<ItemTrackingStatus, ItemTrackingStatus>>>({});
  const [perQaTarget, setPerQaTarget] = useState<Partial<Record<string, boolean>>>({});

  const summaryEntries = getStatusEntries(statusSummary);

  const handleApply = () => {
    onApply(perGroupTarget, perQaTarget);
    setPerGroupTarget({});
    setPerQaTarget({});
  };

  const handleClose = () => {
    setPerGroupTarget({});
    setPerQaTarget({});
    onClose();
  };

  const handleCancel = () => {
    setPerGroupTarget({});
    setPerQaTarget({});
  };

  const hasChanges = Object.keys(perGroupTarget).length > 0 || Object.keys(perQaTarget).length > 0;

  return (
    <ContentSidebar
      isCollapsed={false}
      sx={{
        '@keyframes slideInRight': {
          from: { transform: 'translateX(100%)', opacity: 0 },
          to: { transform: 'translateX(0)', opacity: 1 },
        },
        animation: 'slideInRight 0.25s ease-out',
        flexShrink: 0,
        width: 320,
        alignSelf: 'stretch',
        height: '100%',
        bgcolor: 'primary.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ContentSidebar.Card
        title="Bulk Edit"
        actions={
          <Stack direction="column" spacing={0.5} alignItems="flex-end">
            <IconButton size="small" onClick={handleClose} aria-label="Close bulk edit">
              <CloseIcon sx={{ fontSize: 'medium' }} />
            </IconButton>
          </Stack>
        }
      >
        <Stack spacing={1.5} sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {selectedCount} item{selectedCount === 1 ? '' : 's'} selected
          </Typography>

          {summaryEntries.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Status
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
                {summaryEntries.map(([currentStatus, count], index) => (
                  <Fragment key={currentStatus}>
                    {index > 0 && <Divider sx={{ gridColumn: '1 / -1', my: 0.5 }} />}
                    <Box sx={{ py: 0.5 }}>
                      <StatusBadge
                        color={getStatusColor(currentStatus)}
                        style={{ whiteSpace: 'nowrap', justifyContent: 'center' }}
                      >
                        {count}× {currentStatus}
                      </StatusBadge>
                    </Box>
                    <Box sx={{ py: 0.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id={`group-${currentStatus}-label`}>Change to</InputLabel>
                        <Select
                          labelId={`group-${currentStatus}-label`}
                          label="Change to"
                          value={perGroupTarget[currentStatus] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (isItemTrackingStatus(val)) {
                              setPerGroupTarget((prev) => ({ ...prev, [currentStatus]: val }));
                            }
                          }}
                        >
                          {STATUS_OPTIONS.filter((s) => s !== currentStatus).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Fragment>
                ))}
              </Box>
            </>
          )}

          {(qaSummary.approved > 0 || qaSummary.pending > 0) && (
            <>
              {summaryEntries.length > 0 && <Divider />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                QA
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
                {qaSummary.approved > 0 && (
                  <Fragment key="qa-approved">
                    <Box sx={{ py: 0.5 }}>
                      <StatusBadge color="green" style={{ whiteSpace: 'nowrap', justifyContent: 'center' }}>
                        {qaSummary.approved}× Approved
                      </StatusBadge>
                    </Box>
                    <Box sx={{ py: 0.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="qa-approved-label">Change to</InputLabel>
                        <Select
                          labelId="qa-approved-label"
                          label="Change to"
                          value={perQaTarget['true'] === undefined ? '' : String(perQaTarget['true'])}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setPerQaTarget((prev) => {
                                const n = { ...prev };
                                delete n['true'];
                                return n;
                              });
                            } else {
                              setPerQaTarget((prev) => ({ ...prev, true: val === 'true' }));
                            }
                          }}
                        >
                          <MenuItem value="false">
                            <StatusBadge color="grey">Pending</StatusBadge>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Fragment>
                )}

                {qaSummary.approved > 0 && qaSummary.pending > 0 && <Divider sx={{ gridColumn: '1 / -1', my: 0.5 }} />}

                {qaSummary.pending > 0 && (
                  <Fragment key="qa-pending">
                    <Box sx={{ py: 0.5 }}>
                      <StatusBadge color="grey" style={{ whiteSpace: 'nowrap', justifyContent: 'center' }}>
                        {qaSummary.pending}× Pending
                      </StatusBadge>
                    </Box>
                    <Box sx={{ py: 0.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="qa-pending-label">Change to</InputLabel>
                        <Select
                          labelId="qa-pending-label"
                          label="Change to"
                          value={perQaTarget['false'] === undefined ? '' : String(perQaTarget['false'])}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setPerQaTarget((prev) => {
                                const n = { ...prev };
                                delete n['false'];
                                return n;
                              });
                            } else {
                              setPerQaTarget((prev) => ({ ...prev, false: val === 'true' }));
                            }
                          }}
                        >
                          <MenuItem value="true">
                            <StatusBadge color="green">Approved</StatusBadge>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Fragment>
                )}
              </Box>
            </>
          )}
        </Stack>
      </ContentSidebar.Card>
      <Stack direction="row" spacing={1} sx={{ mt: 'auto', p: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" size="small" disabled={!hasChanges} onClick={handleApply}>
          Apply
        </Button>
      </Stack>
    </ContentSidebar>
  );
}
